// @flow
import {
  AppBar,
  Box,
  Container,
  CssBaseline,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Button,
  Grid,
  LinearProgress,
  Toolbar,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Alert from '@material-ui/lab/Alert';

import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Phone as PhoneIcon,
  Refresh as RefreshIcon,
} from '@material-ui/icons';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useHistory } from 'react-router-dom';
import { AuthContext } from '../context/auth';
import { useSnackbar } from 'notistack';

const baseUrl = process.env.PUBLIC_URL || '';

const useStyles = makeStyles((theme) => ({
  paper: {
    marginTop: theme.spacing(2),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    minHeight: 'calc(100vh - 120px)',
    padding: theme.spacing(2),
  },
  appBarContent: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  logo: {
    height: 32,
    marginRight: theme.spacing(2),
  },
  brandContainer: {
    display: 'flex',
    alignItems: 'center',
    flexGrow: 1,
  },
  brandText: {
    color: theme.palette.text.primary,
    fontWeight: 500,
  },
  poweredBy: {
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.text.secondary,
    marginLeft: 'auto',
  },
  container: {
    margin: '0px !important',
    padding: '0px !important',
    maxWidth: 'none !important',
  },
  card: {
    maxWidth: 600,
    margin: '0 auto',
    padding: theme.spacing(3),
    textAlign: 'center',
  },
  statusIcon: {
    fontSize: '4rem',
    marginBottom: theme.spacing(2),
  },
  success: {
    color: theme.palette.success.main,
  },
  error: {
    color: theme.palette.error.main,
  },
  payphoneHeader: {
    background: 'linear-gradient(135deg, #ff5a1f 0%, #e9531dff 100%)',
    color: 'white',
    padding: theme.spacing(2),
    borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
    marginBottom: theme.spacing(3),
  },
  retryButton: {
    marginTop: theme.spacing(2),
  },
  progressContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  detailsContainer: {
    marginTop: theme.spacing(3),
    textAlign: 'left',
  },
}));

type Props = {};

const PaymentBack = (props: Props) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const location = useLocation();
  const history = useHistory();
  const { doPost } = useContext(AuthContext);
  const { enqueueSnackbar } = useSnackbar();

  const [paymentStatus, setPaymentStatus] = useState('loading'); // loading, success, error
  const [paymentData] = useState(null);
  const [error, setError] = useState(null);
  const [urlParams, setUrlParams] = useState({});

  const checkPaymentStatus = useCallback(
    async (params) => {
      try {
        setPaymentStatus('loading');
        setError(null);
        const processCode = params.clientTransactionId.split('-').slice(0, -1).join('-');

        const response = await doPost('/customer/payment/payphone', {
          id: params.id,
          clientTransactionId: params.clientTransactionId,
          processCode,
        });

        // If we reach here, the request was successful (200 status)
        console.log({ response });

        setPaymentStatus(response.data.status);
        enqueueSnackbar(response.data.message);
      } catch (err) {
        console.error('Error checking payment status:', err);
        setError(err.message || 'Payment failed');
        setPaymentStatus('error');
      }
    },
    [doPost, setPaymentStatus, setError, enqueueSnackbar]
  );

  useEffect(() => {
    // Extract URL parameters
    const searchParams = new URLSearchParams(location.search);
    const params = {
      id: searchParams.get('id'),
      clientTransactionId: searchParams.get('clientTransactionId'),
    };

    setUrlParams(params);

    // Only make API call if we have the required parameters
    if (params.id && params.clientTransactionId) {
      checkPaymentStatus(params);
    } else {
      setError('Missing required payment parameters');
      setPaymentStatus('error');
    }
  }, [location.search, checkPaymentStatus]);

  const handleRetry = () => {
    if (urlParams.id && urlParams.clientTransactionId) {
      checkPaymentStatus(urlParams);
    }
  };

  const handleGoBack = () => {
    history.goBack();
  };

  const renderStatusIcon = () => {
    switch (paymentStatus) {
      case 'success':
        return <CheckCircleIcon className={`${classes.statusIcon} ${classes.success}`} />;
      case 'failure':
        return <ErrorIcon className={`${classes.statusIcon} ${classes.error}`} />;
      default:
        return <CircularProgress className={classes.statusIcon} color="primary" />;
    }
  };

  const renderStatusMessage = () => {
    switch (paymentStatus) {
      case 'success':
        return {
          title: t('Payment Successful!'),
          subtitle: t('Your PayPhone payment has been processed successfully'),
          color: 'success',
        };
      case 'failure':
        return {
          title: t('Payment Failed'),
          subtitle: error || t('There was an issue processing your PayPhone payment'),
          color: 'error',
        };
      default:
        return {
          title: t('Checking Payment Status'),
          subtitle: t('Please wait while we verify your PayPhone payment'),
          color: 'info',
        };
    }
  };

  const statusInfo = renderStatusMessage();

  return (
    <Box>
      <CssBaseline />
      <AppBar position="static" color="default" elevation={1}>
        <Container maxWidth={false} className={classes.container}>
          <Toolbar>
            <Box className={classes.appBarContent}>
              <div className={classes.brandContainer}>
                <img src={`${baseUrl}/images/logo.png`} alt="Logo" className={classes.logo} />
                <div>
                  <Typography variant="h6" className={classes.brandText}>
                    {t('PayPhone Payment Verification')}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {t('EcuaFirma Payment Processing')}
                  </Typography>
                </div>
              </div>
              <Typography variant="caption" className={classes.poweredBy}>
                {t('Powered by eQon')}
              </Typography>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      <div className={classes.paper}>
        <Card className={classes.card}>
          <div className={classes.payphoneHeader}>
            <Typography
              variant="h5"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              <PhoneIcon />
              PayPhone
            </Typography>
            <Typography variant="subtitle1" style={{ opacity: 0.9 }}>
              {t('Payment Verification')}
            </Typography>
          </div>

          <CardContent>
            {renderStatusIcon()}

            <Typography variant="h5" gutterBottom color={statusInfo.color === 'success' ? 'primary' : 'textPrimary'}>
              {statusInfo.title}
            </Typography>

            <Typography variant="body1" color="textSecondary" gutterBottom>
              {statusInfo.subtitle}
            </Typography>

            {paymentStatus === 'loading' && (
              <div className={classes.progressContainer}>
                <LinearProgress color="primary" />
              </div>
            )}

            {paymentData && (
              <div className={classes.detailsContainer}>
                <Alert severity={statusInfo.color} style={{ marginBottom: 16 }}>
                  <Typography variant="subtitle2">{t('Transaction Details')}</Typography>
                </Alert>

                <Grid container spacing={2}>
                  {urlParams.id && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="textSecondary">
                        {t('Payment ID')}
                      </Typography>
                      <Typography variant="body2">{urlParams.id}</Typography>
                    </Grid>
                  )}

                  {urlParams.clientTransactionId && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="textSecondary">
                        {t('Transaction ID')}
                      </Typography>
                      <Typography variant="body2" style={{ wordBreak: 'break-all' }}>
                        {urlParams.clientTransactionId}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </div>
            )}

            <Box mt={3}>
              <Grid container spacing={2} justifyContent="center">
                {paymentStatus === 'error' && (
                  <Grid item>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={handleRetry}
                      startIcon={<RefreshIcon />}
                      className={classes.retryButton}>
                      {t('Retry')}
                    </Button>
                  </Grid>
                )}

                <Grid item>
                  <Button variant="contained" color="primary" onClick={handleGoBack} className={classes.retryButton}>
                    {paymentStatus === 'success' ? t('Continue') : t('Go Back')}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </CardContent>
        </Card>
      </div>
    </Box>
  );
};

export default PaymentBack;
